import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, QrCode, LayoutDashboard, PlusCircle, 
  BarChart3, Settings, LogOut, Bell, 
  ChevronDown, User, Layers, Shield, 
  FileText, Info, HelpCircle, Mail, DollarSign,
  Scan, Globe, Zap, Palette, Sparkles, TrendingUp,
  Users, Activity, Search, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { Footer } from './Footer';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { QRScannerButton } from '@/components/mobile/QRScannerButton';
import { SupportChat } from '@/components/compliance/SupportChat';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { session, signOut } = useAuth();
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const guestLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blog', href: '/blog' },
    { label: 'Support', href: '/support' },
    { label: 'Contact', href: '/contact' },
  ];

  const loggedInLinks: Array<{ label: string; href: string; icon: React.ComponentType<{ className?: string }> }> = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'Create', href: '/create', icon: PlusCircle },
    { label: 'Templates', href: '/templates', icon: Layers },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4 md:px-6",
          isScrolled 
            ? "py-2 bg-background/90 backdrop-blur-xl border-b border-border/40" 
            : "py-4 md:py-5"
        )}
      >
        <div className="max-w-[1400px] mx-auto flex items-center h-full">
          {/* Left: Logo */}
          <div className="flex-1 flex justify-start">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img 
                src="/logo.png" 
                alt="NexQR Logo" 
                className="h-7 md:h-9 w-auto object-contain transition-transform group-hover:scale-[1.03]" 
              />
            </Link>
          </div>

          {/* Center: Nav Pill */}
          <nav className="hidden lg:block">
            <div className="bg-card/80 backdrop-blur-md border border-border/60 rounded-full px-1.5 py-1 flex items-center gap-0.5">
              {!session ? (
                <>
                  {guestLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={cn(
                        "px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200",
                        location.pathname === link.href 
                          ? "text-primary-foreground bg-primary shadow-sm" 
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      )}
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : (
                <>
                  {loggedInLinks.map((link) => {
                    const LinkIcon = link.icon;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={cn(
                          "px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 flex items-center gap-1.5",
                          location.pathname === link.href 
                            ? "text-primary-foreground bg-primary shadow-sm" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        )}
                      >
                        <LinkIcon className="w-3.5 h-3.5" />
                        {link.label}
                      </Link>
                    );
                  })}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="px-4 py-2 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center gap-1 transition-all duration-200">
                        Insights <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-48 p-1.5 rounded-xl border-border/60">
                      <DropdownMenuItem onClick={() => navigate('/analytics')} className="rounded-lg gap-2 cursor-pointer text-[13px]">
                        <BarChart3 className="w-4 h-4" /> Analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/leads')} className="rounded-lg gap-2 cursor-pointer text-[13px]">
                        <Users className="w-4 h-4" /> Leads
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </nav>

          {/* Right: Actions */}
          <div className="flex-1 flex items-center justify-end gap-1.5 sm:gap-2.5">
            <QRScannerButton className="text-muted-foreground hover:text-foreground" />
            
            {!session ? (
              <Link to="/auth" className="hidden sm:block">
                <Button className="rounded-full px-6 h-10 bg-foreground text-background hover:bg-foreground/90 font-semibold text-[13px] transition-all">
                  Get Started
                </Button>
              </Link>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-foreground relative h-9 w-9">
                  <Bell className="w-[18px] h-[18px]" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full border-2 border-background" />
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 group">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center border border-primary/20 group-hover:border-primary/40 transition-all overflow-hidden">
                        {user?.user_metadata?.avatar_url ? (
                          <img src={user.user_metadata.avatar_url} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-primary" />
                        )}
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 p-1.5 rounded-xl border-border/60 bg-popover shadow-lg">
                    <DropdownMenuLabel className="px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5 font-semibold">Account</div>
                      <div className="font-semibold truncate text-sm">{user?.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="rounded-lg gap-2 cursor-pointer py-2 text-[13px]">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-lg gap-2 cursor-pointer py-2 text-[13px]">
                      <Settings className="w-4 h-4" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut} className="rounded-lg gap-2 cursor-pointer py-2 text-[13px] text-destructive focus:text-destructive focus:bg-destructive/10">
                      <LogOut className="w-4 h-4" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Mobile Toggle */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:w-[340px] border-l border-border/40 bg-background p-0">
                  <div className="flex flex-col h-full">
                    <div className="px-6 pt-8 pb-4 border-b border-border/40">
                      <Link to="/" className="flex items-center gap-2">
                        <img src="/logo.png" alt="NexQR Logo" className="h-8 w-auto" />
                      </Link>
                    </div>
                    
                    <nav className="flex flex-col gap-1 px-3 py-4 flex-1">
                      {(!session ? guestLinks : loggedInLinks).map((link) => {
                        const LinkIcon = 'icon' in link ? (link.icon as React.ComponentType<{ className?: string }>) : null;
                        return (
                          <Link
                            key={link.href}
                            to={link.href}
                            className={cn(
                              "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold transition-all",
                              location.pathname === link.href 
                                ? "bg-primary/10 text-primary" 
                                : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                            )}
                          >
                            {LinkIcon && <LinkIcon className="w-5 h-5" />}
                            {link.label}
                          </Link>
                        );
                      })}
                      {session && (
                        <>
                          <div className="h-px bg-border/40 my-2" />
                          <Link to="/analytics" className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all">
                            <BarChart3 className="w-5 h-5" /> Analytics
                          </Link>
                          <Link to="/leads" className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all">
                            <Users className="w-5 h-5" /> Leads
                          </Link>
                        </>
                      )}
                    </nav>
                    
                    <div className="px-4 pb-8 pt-2 border-t border-border/40">
                      {!session ? (
                        <Link to="/auth" className="w-full block">
                          <Button className="w-full rounded-xl h-12 text-base font-semibold bg-foreground text-background hover:bg-foreground/90">
                            Get Started
                          </Button>
                        </Link>
                      ) : (
                        <Button onClick={signOut} variant="outline" className="w-full rounded-xl h-12 text-base font-semibold text-destructive border-destructive/20 hover:bg-destructive/5">
                          <LogOut className="w-4 h-4 mr-2" /> Sign Out
                        </Button>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow pt-20 md:pt-24">
        {children}
      </main>

      <Footer />
      <SupportChat />
      <OnboardingTour />
    </div>
  );
};

export default Layout;
